import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { loginUser } from '../../../store/thunks';
import './Login.css';

const Login: React.FC = () => {
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [formData, setFormData] = useState({ email: '', password: '', phone: '', otp: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  React.useEffect(() => {
    if (location.state?.registeredEmail) {
      setFormData(prev => ({ ...prev, email: location.state.registeredEmail }));
    }
  }, [location.state]);

  // OTP countdown timer
  React.useEffect(() => {
    if (otpTimer > 0) {
      const t = setTimeout(() => setOtpTimer(t => t - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [otpTimer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (errors.general) setErrors(prev => ({ ...prev, general: '' }));
  };

  const handleSendOtp = async () => {
    if (!formData.phone || !/^[6-9]\d{9}$/.test(formData.phone.replace(/\s/g, ''))) {
      setErrors({ phone: 'Enter a valid 10-digit Indian mobile number' });
      return;
    }
    setIsSendingOtp(true);
    // Simulate OTP send (replace with real SMS API integration)
    await new Promise(r => setTimeout(r, 1200));
    setOtpSent(true);
    setOtpTimer(30);
    setIsSendingOtp(false);
    setErrors({});
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { [k: string]: string } = {};
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Enter a valid email address';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }

    setIsLoading(true);
    try {
      const result = await dispatch(loginUser({ email: formData.email, password: formData.password }));
      if (loginUser.fulfilled.match(result)) {
        const role = result.payload?.user?.role;
        const adminRoles = ['SUPER_ADMIN', 'ADMIN', 'INVENTORY_MANAGER'];
        if (adminRoles.includes(role)) {
          navigate('/admin', { replace: true });
        } else {
          navigate(from, { replace: true });
        }
      } else {
        setErrors({ general: (result.payload as string) || 'Invalid email or password.' });
      }
    } catch {
      setErrors({ general: 'Something went wrong. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpSent) { handleSendOtp(); return; }
    if (!formData.otp || formData.otp.length !== 6) {
      setErrors({ otp: 'Please enter the 6-digit verification code' });
      return;
    }
    setIsLoading(true);
    try {
      const emailAlias = `${formData.phone.replace(/[^0-9]/g, '')}@industrybull.com`;
      const result = await dispatch(loginUser({ email: emailAlias, password: 'mobile_otp_user' }));
      if (loginUser.fulfilled.match(result)) {
        dispatch(addNotification({ type: 'success', title: 'Login Successful', message: 'Welcome to IndustryBull!' }));
        const from = (location.state as any)?.from?.pathname || '/';
        navigate(from, { replace: true });
      } else {
        setErrors({ general: 'Verification failed. Please try again.' });
      }
    } catch {
      setErrors({ general: 'An error occurred during verification.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setErrors({ general: 'Google Sign-In requires OAuth setup. Please use email login.' });
  };

  return (
    <div className="auth-page">
      {/* Left Panel – Branding */}
      <div className="auth-left">
        <div className="auth-brand">
          <div className="auth-logo">
            <svg width="36" height="36" viewBox="0 0 40 40" fill="none">
              <rect width="40" height="40" rx="10" fill="#fff" fillOpacity="0.15"/>
              <path d="M8 28L14 12L20 22L26 16L32 28H8Z" fill="white"/>
            </svg>
            <span>IndustryBull</span>
          </div>
          <div className="auth-tagline">
            <h2>India's #1 B2B Industrial Marketplace</h2>
            <p>Access 10,000+ products from verified suppliers. Real-time pricing. Bulk discounts.</p>
          </div>
          <div className="auth-stats">
            <div className="auth-stat"><strong>338+</strong><span>Products</span></div>
            <div className="auth-stat"><strong>100+</strong><span>Categories</span></div>
            <div className="auth-stat"><strong>B2B</strong><span>Pricing</span></div>
          </div>
          <div className="auth-trust">
            <div className="trust-badge">🔒 Secure Payments</div>
            <div className="trust-badge">✅ Verified Sellers</div>
            <div className="trust-badge">🚚 Pan India Delivery</div>
          </div>
        </div>
      </div>

      {/* Right Panel – Login Form */}
      <div className="auth-right">
        <div className="auth-card">
          {/* Header */}
          <div className="auth-card-header">
            <h1>Welcome Back</h1>
            <p>Sign in to your IndustryBull account</p>
          </div>

          {/* Login Method Toggle */}
          <div className="auth-method-toggle">
            <button
              type="button"
              className={loginMethod === 'email' ? 'active' : ''}
              onClick={() => { setLoginMethod('email'); setErrors({}); }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              Email
            </button>
            <button
              type="button"
              className={loginMethod === 'phone' ? 'active' : ''}
              onClick={() => { setLoginMethod('phone'); setErrors({}); setOtpSent(false); }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                <line x1="12" y1="18" x2="12.01" y2="18"/>
              </svg>
              Mobile OTP
            </button>
          </div>

          {/* Error Banner */}
          {errors.general && (
            <div className="auth-error-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {errors.general}
            </div>
          )}

          {/* Email Login Form */}
          {loginMethod === 'email' && (
            <form onSubmit={handleEmailSubmit} className="auth-form" noValidate>
              <div className={`auth-field ${errors.email ? 'has-error' : ''}`}>
                <label htmlFor="email">Email Address</label>
                <div className="auth-input-wrap">
                  <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    autoComplete="email"
                    disabled={isLoading}
                  />
                </div>
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>

              <div className={`auth-field ${errors.password ? 'has-error' : ''}`}>
                <label htmlFor="password">
                  Password
                  <Link to="/auth/forgot-password" className="forgot-link">Forgot password?</Link>
                </label>
                <div className="auth-input-wrap">
                  <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Min 6 characters"
                    autoComplete="current-password"
                    disabled={isLoading}
                  />
                  <button type="button" className="toggle-password" onClick={() => setShowPassword(p => !p)} tabIndex={-1}>
                    {showPassword ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && <span className="field-error">{errors.password}</span>}
              </div>

              <button type="submit" className="auth-submit-btn" disabled={isLoading}>
                {isLoading ? (
                  <><span className="btn-spinner"/>&nbsp;Signing In...</>
                ) : 'Sign In'}
              </button>
            </form>
          )}

          {/* Phone OTP Form */}
          {loginMethod === 'phone' && (
            <form onSubmit={handlePhoneSubmit} className="auth-form" noValidate>
              <div className={`auth-field ${errors.phone ? 'has-error' : ''}`}>
                <label htmlFor="phone">Mobile Number</label>
                <div className="auth-input-wrap phone-input-wrap">
                  <span className="phone-prefix">🇮🇳 +91</span>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="98765 43210"
                    maxLength={10}
                    disabled={otpSent || isLoading}
                  />
                  {!otpSent && (
                    <button
                      type="button"
                      className="send-otp-btn"
                      onClick={handleSendOtp}
                      disabled={isSendingOtp}
                    >
                      {isSendingOtp ? 'Sending...' : 'Send OTP'}
                    </button>
                  )}
                </div>
                {errors.phone && <span className="field-error">{errors.phone}</span>}
              </div>

              {otpSent && (
                <>
                  <div className="otp-sent-msg">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    OTP sent to +91 {formData.phone}
                    {otpTimer > 0 ? (
                      <span className="otp-timer"> · Resend in {otpTimer}s</span>
                    ) : (
                      <button type="button" className="resend-link" onClick={handleSendOtp}>Resend OTP</button>
                    )}
                  </div>

                  <div className={`auth-field ${errors.otp ? 'has-error' : ''}`}>
                    <label htmlFor="otp">Enter 6-digit OTP</label>
                    <div className="auth-input-wrap">
                      <input
                        id="otp"
                        name="otp"
                        type="text"
                        value={formData.otp}
                        onChange={handleChange}
                        placeholder="• • • • • •"
                        maxLength={6}
                        className="otp-input"
                        disabled={isLoading}
                      />
                    </div>
                    {errors.otp && <span className="field-error">{errors.otp}</span>}
                  </div>
                </>
              )}

              <button type="submit" className="auth-submit-btn" disabled={isLoading || isSendingOtp}>
                {isLoading ? (
                  <><span className="btn-spinner"/>&nbsp;Verifying...</>
                ) : otpSent ? 'Verify & Sign In' : 'Send OTP'}
              </button>
            </form>
          )}

          {/* Divider */}
          <div className="auth-divider"><span>OR CONTINUE WITH</span></div>

          {/* Social Login */}
          <div className="auth-social">
            <button type="button" className="social-btn google-btn" onClick={handleGoogleLogin}>
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
          </div>

          {/* Footer */}
          <p className="auth-footer-text">
            New to IndustryBull?{' '}
            <Link to="/auth/register" className="auth-link">Create a free B2B account →</Link>
          </p>
          <p className="auth-footer-text" style={{ marginTop: 10, fontSize: 12, color: '#94a3b8' }}>
            Admin?{' '}
            <Link to="/admin" className="auth-link" style={{ fontSize: 12 }}>Go to Admin Panel →</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
